import React, { type CSSProperties } from 'react';
import {
    DefaultPartnerLink,
    getPartnerChipKey,
    getPartnerInitials,
    getPartnerLogoAlt,
    getPartnerLogoSource,
    getPartnerSchemaTypeUrl,
    isPartnerChip,
    makePartnerSoftAccent,
    partnerTokens,
    renderPartnerIcon,
    resolvePartnerLinkProps,
    toPartnerCssSize,
    type PartnerChip,
    type PartnerColors,
    type PartnerIcon,
    type PartnerIconRenderer,
    type PartnerIconToken,
    type PartnerImageSource,
    type PartnerLinkComponent,
    type PartnerLogo,
    type PartnerSchemaType,
} from '../partner-shared';

export const partnerLinkRailTokens = {
    colors: partnerTokens.colors,
    sizes: {
        maxWidth: partnerTokens.sizes.maxWidth,
        tileWidth: partnerTokens.sizes.railTileWidth,
        minTileWidth: partnerTokens.sizes.railTileMinWidth,
        tileGap: partnerTokens.sizes.tileGap,
    },
} as const;

export type PartnerLinkRailLayout = 'slide' | 'wrap';

export type PartnerLinkRailPartner = {
    id: string | number;
    name: React.ReactNode;
    href: string;
    logo?: PartnerLogo;
    logoSrc?: PartnerImageSource;
    logoAlt?: string;
    description?: React.ReactNode;
    category?: React.ReactNode;
    relationship?: React.ReactNode;
    location?: React.ReactNode;
    tags?: PartnerChip[];
    linkLabel?: React.ReactNode;
    ariaLabel?: string;
    title?: string;
    target?: string;
    rel?: string | false;
    linkRel?: string | false;
    sponsored?: boolean;
    nofollow?: boolean;
    ugc?: boolean;
    schemaType?: PartnerSchemaType;
    icon?: PartnerIcon;
    iconComponent?: PartnerIconRenderer;
    iconToken?: PartnerIconToken;
    accentColor?: string;
    accentColour?: string;
    accentSoftColor?: string;
    accentSoftColour?: string;
    logoBackgroundColor?: string;
    logoBackgroundColour?: string;
};

export type PartnerLinkRailProps = {
    partners: PartnerLinkRailPartner[];
    LinkComponent?: PartnerLinkComponent;
    Link?: PartnerLinkComponent;
    layout?: PartnerLinkRailLayout;
    eyebrow?: React.ReactNode;
    title?: React.ReactNode;
    titleComponent?: 'h1' | 'h2' | 'h3';
    description?: React.ReactNode;
    actionHref?: string;
    actionLabel?: React.ReactNode;
    actionAriaLabel?: string;
    actionTitle?: string;
    actionTarget?: string;
    actionRel?: string | false;
    actionIcon?: PartnerIcon;
    actionIconComponent?: PartnerIconRenderer;
    actionIconToken?: PartnerIconToken;
    ctaLabel?: React.ReactNode;
    disclosure?: React.ReactNode;
    emptyMessage?: React.ReactNode;
    defaultLinkRel?: string | false;
    defaultLinkTarget?: string;
    colors?: PartnerColors;
    colours?: PartnerColors;
    accentColor?: string;
    accentColour?: string;
    backgroundColor?: string;
    backgroundColour?: string;
    surfaceColor?: string;
    surfaceColour?: string;
    textColor?: string;
    textColour?: string;
    maxWidth?: number | string;
    tileWidth?: number | string;
    minTileWidth?: number | string;
    tileGap?: number | string;
    className?: string;
    style?: CSSProperties;
    ariaLabel?: string;
};

type PartnerLinkRailStyle = CSSProperties &
    Record<`--partner-link-rail-${string}`, string | number | undefined>;

type PartnerLinkRailItemStyle = CSSProperties &
    Record<`--partner-link-rail-${string}`, string | number | undefined>;

const partnerLinkRailCss = `
.partner-link-rail {
  --partner-link-rail-bg: ${partnerTokens.colors.background};
  --partner-link-rail-surface: ${partnerTokens.colors.surface};
  --partner-link-rail-surface-soft: ${partnerTokens.colors.surfaceSoft};
  --partner-link-rail-text: ${partnerTokens.colors.text};
  --partner-link-rail-text-soft: ${partnerTokens.colors.textSoft};
  --partner-link-rail-text-muted: ${partnerTokens.colors.textMuted};
  --partner-link-rail-accent: ${partnerTokens.colors.accent};
  --partner-link-rail-accent-soft: ${partnerTokens.colors.accentSoft};
  --partner-link-rail-action: ${partnerTokens.colors.action};
  --partner-link-rail-border: ${partnerTokens.colors.border};
  --partner-link-rail-section-border: ${partnerTokens.colors.borderStrong};
  --partner-link-rail-chip-bg: ${partnerTokens.colors.chipBackground};
  --partner-link-rail-logo-bg: ${partnerTokens.colors.logoBackground};
  --partner-link-rail-shadow: ${partnerTokens.colors.shadow};
  --partner-link-rail-hover-shadow: ${partnerTokens.colors.hoverShadow};
  --partner-link-rail-max-width: ${partnerLinkRailTokens.sizes.maxWidth}px;
  --partner-link-rail-tile-width: ${partnerLinkRailTokens.sizes.tileWidth}px;
  --partner-link-rail-min-tile-width: ${partnerLinkRailTokens.sizes.minTileWidth}px;
  --partner-link-rail-tile-gap: ${partnerLinkRailTokens.sizes.tileGap}px;
  width: 100%;
  min-width: 0;
  overflow: hidden;
  color: var(--partner-link-rail-text);
  background: transparent;
  font-family: ${partnerTokens.typography.fontFamily};
  box-sizing: border-box;
  container-type: inline-size;
}
.partner-link-rail,
.partner-link-rail * {
  box-sizing: border-box;
}
.partner-link-rail-shell {
  width: 100%;
  max-width: var(--partner-link-rail-max-width);
  margin: 0 auto;
  padding: clamp(22px, 2.4vw, 34px);
  border: 1px solid var(--partner-link-rail-section-border);
  border-radius: 14px;
  background: var(--partner-link-rail-bg);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.82);
}
.partner-link-rail-header-row {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 22px;
  align-items: center;
  margin: 0 0 22px;
}
.partner-link-rail-header {
  display: grid;
  max-width: 760px;
  gap: 10px;
}
.partner-link-rail-eyebrow {
  display: inline-flex;
  width: fit-content;
  max-width: 100%;
  align-items: center;
  gap: 8px;
  margin: 0;
  color: var(--partner-link-rail-accent);
  font-size: 11px;
  line-height: 1;
  font-weight: 900;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  overflow-wrap: anywhere;
}
.partner-link-rail-eyebrow::after {
  content: "";
  width: 8px;
  height: 8px;
  border-radius: 999px;
  background: currentColor;
}
.partner-link-rail-title {
  margin: 0;
  color: var(--partner-link-rail-text);
  font-size: clamp(30px, 3.3vw, 48px);
  line-height: 1.08;
  font-weight: 850;
  letter-spacing: 0;
  overflow-wrap: anywhere;
}
.partner-link-rail-title-highlight {
  color: var(--partner-link-rail-accent);
}
.partner-link-rail-description {
  max-width: 680px;
  margin: 0;
  color: var(--partner-link-rail-text-soft);
  font-size: 15px;
  line-height: 1.55;
  font-weight: 500;
  letter-spacing: 0;
}
.partner-link-rail-action {
  display: inline-flex;
  min-height: 50px;
  align-items: center;
  justify-content: center;
  gap: 10px;
  border: 1px solid color-mix(in srgb, var(--partner-link-rail-action) 42%, var(--partner-link-rail-border));
  border-radius: 8px;
  background: color-mix(in srgb, var(--partner-link-rail-surface) 92%, var(--partner-link-rail-action) 8%);
  color: var(--partner-link-rail-action);
  font-size: 14px;
  line-height: 1;
  font-weight: 850;
  letter-spacing: 0;
  padding: 0 18px;
  text-decoration: none;
  box-shadow: 0 10px 22px rgba(17, 24, 39, 0.04);
  transition: transform 170ms ease, border-color 170ms ease, box-shadow 170ms ease;
}
.partner-link-rail-action:hover {
  transform: translateY(-1px);
  border-color: var(--partner-link-rail-action);
  box-shadow: 0 14px 30px rgba(17, 24, 39, 0.08);
}
.partner-link-rail-action:focus-visible {
  outline: 3px solid color-mix(in srgb, var(--partner-link-rail-action) 28%, transparent);
  outline-offset: 3px;
}
.partner-link-rail-action-text {
  min-width: 0;
  overflow-wrap: anywhere;
}
.partner-link-rail-track {
  min-width: 0;
  margin: 0;
  padding: 2px 2px 16px;
  list-style: none;
}
.partner-link-rail-track--slide {
  display: flex;
  gap: var(--partner-link-rail-tile-gap);
  align-items: stretch;
  overflow-x: auto;
  overflow-y: hidden;
  scroll-padding-left: 2px;
  scroll-snap-type: x proximity;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: thin;
}
.partner-link-rail-track--wrap {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(100%, var(--partner-link-rail-min-tile-width)), 1fr));
  gap: var(--partner-link-rail-tile-gap);
  padding-bottom: 2px;
}
.partner-link-rail-item {
  min-width: 0;
  display: flex;
  align-items: stretch;
}
.partner-link-rail-track--slide .partner-link-rail-item {
  flex: 0 0 min(86vw, var(--partner-link-rail-tile-width));
  scroll-snap-align: start;
}
.partner-link-rail-card {
  --partner-link-rail-card-accent: var(--partner-link-rail-accent);
  --partner-link-rail-card-accent-soft: var(--partner-link-rail-accent-soft);
  display: grid;
  position: relative;
  width: 100%;
  min-width: 0;
  min-height: 100%;
  gap: 14px;
  align-content: start;
  overflow: hidden;
  border: 1px solid var(--partner-link-rail-border);
  border-radius: 8px;
  background: var(--partner-link-rail-surface);
  color: var(--partner-link-rail-text);
  padding: 18px;
  text-decoration: none;
  box-shadow: var(--partner-link-rail-shadow);
  transition: transform 180ms ease, border-color 180ms ease, box-shadow 180ms ease;
}
.partner-link-rail-card::before {
  content: "";
  position: absolute;
  inset: 0 0 auto;
  height: 4px;
  background: var(--partner-link-rail-card-accent);
}
.partner-link-rail-card:hover {
  transform: translateY(-2px);
  border-color: color-mix(in srgb, var(--partner-link-rail-card-accent) 28%, var(--partner-link-rail-border));
  box-shadow: var(--partner-link-rail-hover-shadow);
}
.partner-link-rail-card:focus-visible {
  outline: 3px solid color-mix(in srgb, var(--partner-link-rail-card-accent) 34%, transparent);
  outline-offset: 3px;
}
.partner-link-rail-card-top {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr);
  gap: 13px;
  align-items: center;
  min-width: 0;
}
.partner-link-rail-logo {
  display: flex;
  width: 58px;
  height: 58px;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  border: 1px solid color-mix(in srgb, var(--partner-link-rail-card-accent) 12%, var(--partner-link-rail-border));
  border-radius: 8px;
  background: var(--partner-link-rail-card-logo-bg, var(--partner-link-rail-logo-bg));
  color: var(--partner-link-rail-card-accent);
  font-size: 18px;
  line-height: 1;
  font-weight: 900;
  letter-spacing: 0;
}
.partner-link-rail-logo-image {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: contain;
  object-position: center;
  padding: 8px;
}
.partner-link-rail-logo-fallback {
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
.partner-link-rail-card-heading {
  min-width: 0;
}
.partner-link-rail-card-title {
  margin: 0;
  color: var(--partner-link-rail-text);
  font-size: 17px;
  line-height: 1.18;
  font-weight: 850;
  letter-spacing: 0;
  overflow-wrap: anywhere;
}
.partner-link-rail-card-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 6px 8px;
  margin-top: 7px;
  color: var(--partner-link-rail-text-muted);
  font-size: 12px;
  line-height: 1.3;
  font-weight: 700;
  letter-spacing: 0;
}
.partner-link-rail-card-meta span {
  min-width: 0;
  overflow-wrap: anywhere;
}
.partner-link-rail-card-meta span + span::before {
  content: "/";
  margin-right: 8px;
  color: color-mix(in srgb, var(--partner-link-rail-text-muted) 58%, transparent);
}
.partner-link-rail-relationship {
  display: inline-flex;
  width: fit-content;
  max-width: 100%;
  align-items: center;
  gap: 7px;
  border-radius: 999px;
  background: var(--partner-link-rail-card-accent-soft);
  color: var(--partner-link-rail-card-accent);
  padding: 7px 10px;
  font-size: 11px;
  line-height: 1;
  font-weight: 850;
  letter-spacing: 0;
  overflow-wrap: anywhere;
}
.partner-link-rail-description-text {
  margin: 0;
  color: var(--partner-link-rail-text-soft);
  font-size: 14px;
  line-height: 1.5;
  font-weight: 500;
  letter-spacing: 0;
  overflow-wrap: anywhere;
}
.partner-link-rail-chip-list {
  display: flex;
  min-width: 0;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 1px;
}
.partner-link-rail-chip {
  display: inline-flex;
  max-width: 100%;
  align-items: center;
  border-radius: 999px;
  background: var(--partner-link-rail-chip-surface, var(--partner-link-rail-chip-bg));
  color: var(--partner-link-rail-chip-text, var(--partner-link-rail-text-soft));
  padding: 7px 10px;
  font-size: 11px;
  line-height: 1;
  font-weight: 750;
  letter-spacing: 0;
  overflow-wrap: anywhere;
}
.partner-link-rail-card-cta {
  display: inline-flex;
  width: 100%;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-top: auto;
  padding-top: 2px;
  color: var(--partner-link-rail-card-accent);
  font-size: 13px;
  line-height: 1;
  font-weight: 900;
  letter-spacing: 0;
}
.partner-link-rail-card-cta-text {
  min-width: 0;
  overflow-wrap: anywhere;
}
.partner-link-rail-card:hover .partner-link-rail-card-cta .partner-link-rail-icon {
  transform: translateX(3px);
}
.partner-link-rail-icon,
.partner-link-rail-icon svg {
  display: block;
  width: var(--partner-link-rail-icon-size, 16px);
  height: var(--partner-link-rail-icon-size, 16px);
  flex: 0 0 auto;
}
.partner-link-rail-icon {
  transition: transform 180ms ease;
}
.partner-link-rail-empty {
  display: flex;
  min-height: 132px;
  align-items: center;
  justify-content: center;
  border: 1px dashed var(--partner-link-rail-border);
  border-radius: 8px;
  background: color-mix(in srgb, var(--partner-link-rail-surface) 76%, var(--partner-link-rail-accent-soft));
  color: var(--partner-link-rail-text-muted);
  padding: 22px;
  text-align: center;
  font-size: 15px;
  line-height: 1.45;
  font-weight: 750;
}
.partner-link-rail-disclosure {
  max-width: 780px;
  margin: 16px 0 0;
  color: var(--partner-link-rail-text-muted);
  font-size: 12px;
  line-height: 1.55;
  font-weight: 550;
  letter-spacing: 0;
}
@supports not (color: color-mix(in srgb, red 50%, white)) {
  .partner-link-rail-card:hover {
    border-color: var(--partner-link-rail-card-accent);
  }
}
@container (max-width: 760px) {
  .partner-link-rail-shell {
    padding: 22px;
  }
  .partner-link-rail-header-row {
    grid-template-columns: 1fr;
    gap: 16px;
  }
  .partner-link-rail-title {
    font-size: 32px;
  }
  .partner-link-rail-action {
    width: fit-content;
    min-height: 48px;
  }
}
@container (max-width: 460px) {
  .partner-link-rail-shell {
    padding: 18px;
  }
  .partner-link-rail-track--slide .partner-link-rail-item {
    flex-basis: min(88vw, var(--partner-link-rail-tile-width));
  }
  .partner-link-rail-card {
    padding: 16px;
  }
  .partner-link-rail-logo {
    width: 52px;
    height: 52px;
  }
}
`;

function PartnerLogoMark({
    partner,
}: {
    partner: PartnerLinkRailPartner;
}) {
    const logoSrc = getPartnerLogoSource(partner.logo, partner.logoSrc);
    const dimensions = partner.logo?.src
        ? {
              width:
                  partner.logo.width ||
                  (typeof partner.logo.src === 'object' ? partner.logo.src.width : undefined),
              height:
                  partner.logo.height ||
                  (typeof partner.logo.src === 'object' ? partner.logo.src.height : undefined),
          }
        : { width: partner.logo?.width, height: partner.logo?.height };

    return (
        <span className="partner-link-rail-logo" aria-hidden={logoSrc ? undefined : true}>
            {logoSrc ? (
                <img
                    className="partner-link-rail-logo-image"
                    src={logoSrc}
                    alt={getPartnerLogoAlt({
                        logo: partner.logo,
                        logoAlt: partner.logoAlt,
                        name: partner.name,
                    })}
                    width={dimensions.width}
                    height={dimensions.height}
                    loading={partner.logo?.loading || 'lazy'}
                    decoding={partner.logo?.decoding || 'async'}
                    itemProp="logo"
                    style={{
                        objectFit: partner.logo?.objectFit,
                        objectPosition: partner.logo?.objectPosition,
                    }}
                />
            ) : (
                <span className="partner-link-rail-logo-fallback">
                    {getPartnerInitials(partner.name)}
                </span>
            )}
        </span>
    );
}

function shouldRender(value: React.ReactNode) {
    return value !== undefined && value !== null && value !== false && value !== '';
}

export default function PartnerLinkRail({
    partners,
    LinkComponent,
    Link,
    layout = 'slide',
    eyebrow,
    title,
    titleComponent = 'h2',
    description,
    actionHref,
    actionLabel,
    actionAriaLabel,
    actionTitle,
    actionTarget,
    actionRel,
    actionIcon,
    actionIconComponent,
    actionIconToken = 'externalLink',
    ctaLabel = 'Visit partner',
    disclosure,
    emptyMessage = 'No partners available.',
    defaultLinkRel,
    defaultLinkTarget,
    colors,
    colours,
    accentColor,
    accentColour,
    backgroundColor,
    backgroundColour,
    surfaceColor,
    surfaceColour,
    textColor,
    textColour,
    maxWidth,
    tileWidth,
    minTileWidth,
    tileGap,
    className,
    style,
    ariaLabel,
}: PartnerLinkRailProps) {
    const ComponentLink = LinkComponent || Link || DefaultPartnerLink;
    const mergedColors = { ...colors, ...colours };
    const resolvedAccent =
        accentColor || accentColour || mergedColors.accent || partnerTokens.colors.accent;
    const resolvedAccentSoft = mergedColors.accentSoft || makePartnerSoftAccent(resolvedAccent);
    const hasHeader = !!(eyebrow || title || description);
    const hasAction = !!(actionHref && actionLabel);
    const TitleTag = titleComponent;
    const sectionStyle: PartnerLinkRailStyle = {
        ...style,
        '--partner-link-rail-bg':
            backgroundColor || backgroundColour || mergedColors.background,
        '--partner-link-rail-surface': surfaceColor || surfaceColour || mergedColors.surface,
        '--partner-link-rail-surface-soft': mergedColors.surfaceSoft,
        '--partner-link-rail-text': textColor || textColour || mergedColors.text,
        '--partner-link-rail-text-soft': mergedColors.textSoft,
        '--partner-link-rail-text-muted': mergedColors.textMuted,
        '--partner-link-rail-accent': resolvedAccent,
        '--partner-link-rail-accent-soft': resolvedAccentSoft,
        '--partner-link-rail-action': mergedColors.action,
        '--partner-link-rail-border': mergedColors.border,
        '--partner-link-rail-section-border': mergedColors.borderStrong,
        '--partner-link-rail-chip-bg': mergedColors.chipBackground,
        '--partner-link-rail-logo-bg': mergedColors.logoBackground,
        '--partner-link-rail-shadow': mergedColors.shadow,
        '--partner-link-rail-hover-shadow': mergedColors.hoverShadow,
        '--partner-link-rail-max-width': toPartnerCssSize(maxWidth),
        '--partner-link-rail-tile-width': toPartnerCssSize(tileWidth),
        '--partner-link-rail-min-tile-width': toPartnerCssSize(minTileWidth),
        '--partner-link-rail-tile-gap': toPartnerCssSize(tileGap),
    };
    const actionLinkProps = resolvePartnerLinkProps({
        rel: actionRel,
        target: actionTarget,
        defaultRel: defaultLinkRel,
        defaultTarget: defaultLinkTarget,
    });

    return (
        <section
            className={['partner-link-rail', className].filter(Boolean).join(' ')}
            style={sectionStyle}
            aria-label={ariaLabel}
            data-component="partner-link-rail"
        >
            <style>{partnerLinkRailCss}</style>

            <div className="partner-link-rail-shell">
                {hasHeader || hasAction ? (
                    <div className="partner-link-rail-header-row">
                        {hasHeader ? (
                            <header className="partner-link-rail-header">
                                {eyebrow ? (
                                    <p className="partner-link-rail-eyebrow">{eyebrow}</p>
                                ) : null}
                                {title ? (
                                    <TitleTag className="partner-link-rail-title">
                                        {title}
                                    </TitleTag>
                                ) : null}
                                {description ? (
                                    <p className="partner-link-rail-description">{description}</p>
                                ) : null}
                            </header>
                        ) : (
                            <span aria-hidden="true" />
                        )}

                        {hasAction ? (
                            <ComponentLink
                                href={actionHref || '#'}
                                className="partner-link-rail-action"
                                aria-label={actionAriaLabel}
                                title={actionTitle}
                                {...actionLinkProps}
                            >
                                {renderPartnerIcon({
                                    icon: actionIcon,
                                    iconComponent: actionIconComponent,
                                    iconToken: actionIconToken,
                                    size: 18,
                                    className: 'partner-link-rail-icon',
                                    cssVariable: '--partner-link-rail-icon-size',
                                })}
                                <span className="partner-link-rail-action-text">
                                    {actionLabel}
                                </span>
                                {renderPartnerIcon({
                                    iconToken: 'arrowRight',
                                    size: 16,
                                    className: 'partner-link-rail-icon',
                                    cssVariable: '--partner-link-rail-icon-size',
                                })}
                            </ComponentLink>
                        ) : null}
                    </div>
                ) : null}

                {partners.length ? (
                    <ul
                        className={[
                            'partner-link-rail-track',
                            `partner-link-rail-track--${layout}`,
                        ].join(' ')}
                    >
                        {partners.map((partner) => {
                            const partnerAccent =
                                partner.accentColor || partner.accentColour || resolvedAccent;
                            const partnerAccentSoft =
                                partner.accentSoftColor ||
                                partner.accentSoftColour ||
                                makePartnerSoftAccent(partnerAccent) ||
                                resolvedAccentSoft;
                            const partnerLinkProps = resolvePartnerLinkProps({
                                rel: partner.rel,
                                linkRel: partner.linkRel,
                                target: partner.target,
                                sponsored: partner.sponsored,
                                nofollow: partner.nofollow,
                                ugc: partner.ugc,
                                defaultRel: defaultLinkRel,
                                defaultTarget: defaultLinkTarget,
                            });
                            const itemStyle: PartnerLinkRailItemStyle = {
                                '--partner-link-rail-card-accent': partnerAccent,
                                '--partner-link-rail-card-accent-soft': partnerAccentSoft,
                                '--partner-link-rail-card-logo-bg':
                                    partner.logoBackgroundColor ||
                                    partner.logoBackgroundColour ||
                                    partner.logo?.backgroundColor ||
                                    partner.logo?.backgroundColour,
                            };

                            return (
                                <li
                                    className="partner-link-rail-item"
                                    key={partner.id}
                                    itemScope
                                    itemType={getPartnerSchemaTypeUrl(partner.schemaType)}
                                >
                                    <ComponentLink
                                        href={partner.href}
                                        className="partner-link-rail-card"
                                        style={itemStyle}
                                        aria-label={partner.ariaLabel}
                                        title={partner.title}
                                        itemProp="url"
                                        {...partnerLinkProps}
                                    >
                                        <div className="partner-link-rail-card-top">
                                            <PartnerLogoMark partner={partner} />
                                            <div className="partner-link-rail-card-heading">
                                                <h3
                                                    className="partner-link-rail-card-title"
                                                    itemProp="name"
                                                >
                                                    {partner.name}
                                                </h3>
                                                {partner.category || partner.location ? (
                                                    <p className="partner-link-rail-card-meta">
                                                        {shouldRender(partner.category) ? (
                                                            <span>{partner.category}</span>
                                                        ) : null}
                                                        {shouldRender(partner.location) ? (
                                                            <span>{partner.location}</span>
                                                        ) : null}
                                                    </p>
                                                ) : null}
                                            </div>
                                        </div>

                                        {partner.relationship ? (
                                            <span className="partner-link-rail-relationship">
                                                {renderPartnerIcon({
                                                    icon: partner.icon,
                                                    iconComponent: partner.iconComponent,
                                                    iconToken: partner.iconToken || 'handshake',
                                                    size: 14,
                                                    className: 'partner-link-rail-icon',
                                                    cssVariable: '--partner-link-rail-icon-size',
                                                })}
                                                <span>{partner.relationship}</span>
                                            </span>
                                        ) : null}

                                        {partner.description ? (
                                            <p
                                                className="partner-link-rail-description-text"
                                                itemProp="description"
                                            >
                                                {partner.description}
                                            </p>
                                        ) : null}

                                        {partner.tags?.length ? (
                                            <div className="partner-link-rail-chip-list">
                                                {partner.tags.map((tag, index) => {
                                                    const chip = isPartnerChip(tag)
                                                        ? tag
                                                        : { label: tag };
                                                    const chipStyle = {
                                                        '--partner-link-rail-chip-surface':
                                                            chip.backgroundColor ||
                                                            chip.backgroundColour,
                                                        '--partner-link-rail-chip-text':
                                                            chip.textColor ||
                                                            chip.textColour ||
                                                            chip.color ||
                                                            chip.colour,
                                                    } as CSSProperties;

                                                    return (
                                                        <span
                                                            className="partner-link-rail-chip"
                                                            style={chipStyle}
                                                            aria-label={chip.ariaLabel}
                                                            key={getPartnerChipKey(tag, index)}
                                                        >
                                                            {chip.label}
                                                        </span>
                                                    );
                                                })}
                                            </div>
                                        ) : null}

                                        <span className="partner-link-rail-card-cta">
                                            <span className="partner-link-rail-card-cta-text">
                                                {partner.linkLabel || ctaLabel}
                                            </span>
                                            {renderPartnerIcon({
                                                iconToken: 'arrowRight',
                                                size: 16,
                                                className: 'partner-link-rail-icon',
                                                cssVariable: '--partner-link-rail-icon-size',
                                            })}
                                        </span>
                                    </ComponentLink>
                                </li>
                            );
                        })}
                    </ul>
                ) : (
                    <div className="partner-link-rail-empty">{emptyMessage}</div>
                )}

                {disclosure ? (
                    <p className="partner-link-rail-disclosure">{disclosure}</p>
                ) : null}
            </div>
        </section>
    );
}
